import { Box, Paper, Typography, IconButton, Menu, MenuItem, Fab } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ICarrinhoItem } from "..";
import { Fragment, useState } from 'react';
import { formatarValorMonetario } from "../../../../../../../helpers/masks";
import DialogEditItem from "./DialogEditItem";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

export default function CarrinhoCard({
    itens,
    onRemoverItem,
    onEditarItem,
    onClickCheckout
}: CarrinhoCardProps) {

    const [menuState, setMenuState] = useState<{
        anchorEl: HTMLElement | null;
        itemId: string | null;
        lojaId: number | null;
    }>({
        anchorEl: null,
        itemId: null,
        lojaId: null
    });
    const [openDialogEditItem, setOpenDialogEditItem] = useState<ICarrinhoItem | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>, itemId: string, lojaId: number) => {
        setMenuState({
            anchorEl: event.currentTarget,
            itemId: itemId,
            lojaId: lojaId
        });
    };

    const handleClose = () => {
        setMenuState({
            anchorEl: null,
            itemId: null,
            lojaId: null
        });
    };

    const handleEdit = (item: ICarrinhoItem) => {
        setOpenDialogEditItem(item);
        handleClose();
    };

    const handleRemove = () => {
        if (menuState.itemId && menuState.lojaId) {
            onRemoverItem(menuState.itemId, menuState.lojaId);
            handleClose();
        }
    };

    // Agrupa os itens por loja
    const itensPorLoja = itens.reduce<Record<number, IItensPorLoja>>((acc, item) => {
        const lojaId = item.loja.id;
        if (!acc[lojaId]) {
            acc[lojaId] = {
                loja: item.loja,
                itens: []
            };
        }
        acc[lojaId].itens.push(item);
        acc[lojaId].itens.sort((a, b) => a.nome.localeCompare(b.nome));
        return acc;
    }, {});


    const itensPerloja = Object.values(itensPorLoja);

    const checkoutDisabled = Object.values(itensPorLoja).some(lojaGroup => {
        const itensPorId = lojaGroup.itens.reduce<Record<string, number>>((acc, item) => {
            acc[item.id] = (acc[item.id] || 0) + item.quantidade;
            return acc;
        }, {});

        return lojaGroup.itens.some(item => itensPorId[item.id] > item.loja.estoque);
    });

    return (
        <>
            {
                itens.length > 0 && (
                    <Box component={Paper} mt={2}>
                        <Typography variant='h6' p={2}>Carrinho</Typography>
                        <Box mx={2}>
                            {itensPerloja.map((itemLoja, index) => (
                                <Fragment key={index}>
                                    <Typography
                                        mb={1}
                                        variant="subtitle2"
                                    >{itemLoja.loja.nome}</Typography>
                                    {
                                        itemLoja.itens.map((item) => (
                                            <Box
                                                key={item.id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    mb: 2,
                                                    p: 2,
                                                    border: `1px solid ${item.quantidade > item.loja.estoque ? 'red' : '#e0e0e0'}`,
                                                    borderRadius: 1
                                                }}
                                            >
                                                <Box>
                                                    <Typography sx={{ fontWeight: 'bold' }}>{item.nome}</Typography>
                                                    <Typography
                                                        variant="subtitle1"
                                                        color="text.secondary"
                                                    >
                                                        {
                                                            item.desconto > 0 ? `${item.quantidade} x (${formatarValorMonetario(item.preco)} - ${formatarValorMonetario(item.desconto)}) = ${formatarValorMonetario(item.quantidade * (item.preco - item.desconto))}` :
                                                                `${item.quantidade} x ${formatarValorMonetario(item.preco)} = ${formatarValorMonetario(item.quantidade * item.preco)}`
                                                        }
                                                    </Typography>
                                                </Box>

                                                <IconButton
                                                    color="primary"
                                                    onClick={(e) => handleClick(e, item.id, item.loja.id)}
                                                    size="small"
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>

                                                <Menu
                                                    anchorEl={menuState.anchorEl}
                                                    open={
                                                        Boolean(menuState.anchorEl) &&
                                                        menuState.itemId === item.id &&
                                                        menuState.lojaId === item.loja.id
                                                    }
                                                    onClose={handleClose}
                                                >
                                                    <MenuItem onClick={() => handleEdit(item)}>
                                                        <EditIcon sx={{ mr: 1 }} fontSize="small" />
                                                        Editar
                                                    </MenuItem>
                                                    <MenuItem onClick={handleRemove}>
                                                        <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" />
                                                        Remover
                                                    </MenuItem>
                                                </Menu>
                                            </Box>
                                        ))}
                                </Fragment>
                            ))}

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    borderTop: '2px solid #e0e0e0',
                                    py: 2,
                                    px: 1,
                                    mt: 2
                                }}
                            >
                                <Typography variant="h6">Total</Typography>
                                <Typography variant="h6">
                                    R$ {itens.reduce((total, item) =>
                                    total + item.quantidade * (item.preco - item.desconto), 0
                                ).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </Box>

                            <Fab 
                                variant="extended" 
                                size="medium" 
                                color="primary"
                                disabled={checkoutDisabled}
                                onClick={onClickCheckout}
                                sx={{
                                    position: 'fixed',
                                    bottom: 16,
                                    right: 16
                                }}
                            >
                                <ShoppingCartOutlinedIcon sx={{ mr: 1 }} />
                                Ir para checkout
                            </Fab>
                        </Box>
                    </Box>
                )
            }

            {
                itens.length === 0 && (
                    <Box
                        sx={{
                            border: '2px dashed #2e7d32',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            mt: 2
                        }}
                    >
                        <Typography sx={{ color: '#2e7d32' }}>
                            Escolha o produtos ao lado para adicionar itens ao carrinho e criar um pedido
                        </Typography>
                    </Box>
                )}

            {
                openDialogEditItem && (
                    <DialogEditItem
                        {...{ onEditarItem }}
                        open={Boolean(openDialogEditItem)}
                        onClose={() => setOpenDialogEditItem(null)}
                        item={openDialogEditItem}
                    />
                )
            }
        </>
    );
}


interface CarrinhoCardProps {
    itens: ICarrinhoItem[];
    onRemoverItem: (id: string, loja_id: number) => void
    onEditarItem: (novoItem: ICarrinhoItem) => void;
    onEditarMultiplosItens: (novosItens: ICarrinhoItem[]) => void;
    onClickCheckout: () => void;
}

export interface IItensPorLoja {
    loja: ILoja;
    itens: ICarrinhoItem[];
}

export interface ILoja {
    id: number;
    nome: string;
}